import template from 'url-template';

import { get, groupBy, isEmpty, orderBy } from 'lodash';

import { setBobLoading } from './bobActions';

import axios from 'axios';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';

import { FETCH_ALL_ROLES, FETCH_ALL_ACTIVE_SCOPES } from '../actions/actionTypes';

import { enqueueSnackbar, handleNetworkError } from './applicationMessageActions';
import { setProgressBarLoading } from './coreActions';

const FetchAllRolesUrl = template.parse('/role/get/allroles{?limit,page,searchFilter,sort}');
const FetchAllScopesUrl = template.parse('/scope/all/available/scopes');
const CreateRoleUrl = template.parse('/role/create');
const GetRoleUrl = template.parse('/role/get/{roleId}');
const UpdateRoleUrl = template.parse('/role/update');
const UpdateRoleStatusUrl = template.parse('/role/bulk/update/status');
const RolesScopes = template.parse('/usercontext/get/applications/roles/scopes');
const UserContextWithScopes = template.parse('/usercontext/users/{userId}/companies/{companyId}');
const DeleteUserScopes = template.parse('/usercontext/delete/users/{userId}/companies/{companyId}');

export const fetchAllRoles = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit = 1000;
    options.page = 1;
    options.searchFilter = '';
    options.sort = '';

    dispatch(setBobLoading('fetchAllRoles'));

    try {
        const response = await k2aBaseApi.get(
            FetchAllRolesUrl.expand({
                ...options
            }),
            header
        );

        const data = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        dispatch({
            type:    FETCH_ALL_ROLES,
            payload: {
                data: orderBy(data, [(role) => role.roleName.toLowerCase()], 'asc'),
                meta
            }
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchAllRoles'));
        dispatch(setProgressBarLoading(false));
    }
};

export const fetchAllScopes = () => async (dispatch, getState) => {
    const header = await awsHeader();
    
    dispatch(setBobLoading('fetchAllScopes'));

    try {
        const response = await k2aBaseApi.get(
            FetchAllScopesUrl.expand({}),
            header
        );

        const data = get(response, 'data', []);
        
        // operations field should have object with id & checked
        data.forEach((item, id) => {
            item.id = id;
            let tempOperations = {};

            if (item.operations) {
                Object.keys(item.operations).forEach(operation => {
                    tempOperations[operation] = {
                        id:      item.operations[operation],
                        checked: false
                    };
                });
            }
        
            item.operations = tempOperations;
        });

        const groupedData = groupBy(data, 'applicationName');
        let tempData = [];
        
        // process data for TreeList. name, id required
        Object.keys(groupedData).forEach(key => {
            groupedData[key].forEach(item => {
                item.name = item.scopeFunction;
            });

            tempData.push({
                name:       key,
                id:         groupedData[key][0].applicationId,
                subItems:   groupedData[key],
                expandable: true
            });
        });

        dispatch({
            type:    FETCH_ALL_ACTIVE_SCOPES,
            payload: tempData
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchAllScopes'));
        dispatch(setProgressBarLoading(false));
    }
};

export const createRole = body => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('createRole'));

    try {
        await k2aBaseApi.post(
            CreateRoleUrl.expand({}),
            body,
            header
        );

        dispatch(enqueueSnackbar('Role created successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    } finally {
        dispatch(setBobLoading('createRole'));
    }
};

export const updateRole = body => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('updateRole'));

    try {
        await k2aBaseApi.put(
            UpdateRoleUrl.expand({}),
            body,
            header
        );

        dispatch(enqueueSnackbar('Role updated successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    } finally {
        dispatch(setBobLoading('updateRole'));
    }
};

export const fetchRole = roleId => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchRole'));

    try {
        const response = await k2aBaseApi.get(
            GetRoleUrl.expand({
                roleId
            }),
            header
        );
        
        const data = get(response, 'data', []);
        
        // operations field should have object with id & checked
        data.scopes.forEach((item, id) => {
            item.id = id;
            let tempOperations = {};
        
            Object.keys(item.operations).forEach(operation => {
                tempOperations[operation] = {
                    id:      item.operations[operation],
                    checked: item.operations[operation] ? true : false
                };
            });
        
            item.operations = tempOperations;
        });
        
        const groupedData = groupBy(data.scopes, 'applicationName');
        let tempData = [];
                
        // process data for TreeList. name, id required
        Object.keys(groupedData).forEach(key => {
            groupedData[key].forEach(item => {
                item.name = item.scopeFunction;
            });
        
            tempData.push({
                name:       key,
                id:         groupedData[key][0].applicationId,
                subItems:   groupedData[key],
                expandable: true
            });
        });
        
        data.scopes = tempData;
        return data;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchRole'));
        dispatch(setProgressBarLoading(false));
    }
};

export const updateRoleStatus = body => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('updateRoleStatus'));

    try {
        await k2aBaseApi.put(
            UpdateRoleStatusUrl.expand({}),
            body,
            header
        );

        dispatch(enqueueSnackbar('Role status updated successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    } finally {
        dispatch(setBobLoading('updateRoleStatus'));
    }
};

export const fetchRoleScopes = (applicationIds, roleIds, userId, clientId, siteId, isEdit = false) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchRoleScopes'));

    const body = {
        'applicationIds': [
            ...applicationIds
        ],
        'roleIds':  roleIds,
        'userId':   userId,
        'clientId': clientId,
        'siteId':   siteId
    };

    try {
        const response = await k2aBaseApi.post(
            RolesScopes.expand({}),
            body,
            header
        );
        
        const data = get(response, 'data', []);

        // operations field should have object with id & checked
        data.forEach((item, id) => {
            item.id = id;
            let tempOperations = {};
        
            Object.keys(item.operations).forEach(operation => {
                tempOperations[operation] = {
                    id:      item.operations[operation],
                    checked: isEdit && item.operations[operation] ? true : false
                };
            });
        
            item.operations = tempOperations;
        });
        
        const groupedData = groupBy(data, 'applicationName');
        let tempData = [];
                
        // process data for TreeList. name, id required
        Object.keys(groupedData).forEach(key => {
            groupedData[key].forEach(item => {
                item.name = item.scopeFunction;
            });
        
            tempData.push({
                name:       key,
                id:         groupedData[key][0].applicationId,
                subItems:   groupedData[key],
                expandable: true
            });
        });
        
        return tempData;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchRoleScopes'));
        dispatch(setProgressBarLoading(false));
    }
};

export const updateUserContextWithScopes = (userId, companyId, payload) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('updateUserContextWithScopes'));
    
    try {
        let promises = [];
        if (payload.siteId === null) {
            const body = {
                'roleIds':    payload.roleIds,
                'clientId':   payload.clientId,
                'siteId':     payload.siteId,
                'userScopes': payload.userScopes
            };

            await k2aBaseApi.post(
                UserContextWithScopes.expand({
                    userId,
                    companyId
                }),
                body,
                header
            );
        } else if (!isEmpty(payload.siteId)) {
            payload.siteId.forEach(id => {
                const body = {
                    'roleIds':    payload.roleIds,
                    'clientId':   payload.clientId,
                    'siteId':     id,
                    'userScopes': payload.userScopes
                };

                promises.push(
                    k2aBaseApi.post(
                        UserContextWithScopes.expand({
                            userId,
                            companyId
                        }),
                        body,
                        header
                    ));
            });

            let results = [];

            await Promise.all(promises.map(p => p))
                .then(axios.spread((...result) => {
                    result.forEach(r => {
                        results.push(r);
                    });
                }));
        }

        dispatch(enqueueSnackbar('User permission updated successfully.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('updateUserContextWithScopes'));
        dispatch(setProgressBarLoading(false));
    }
};

export const deleteExistingUserScopes = (userId, companyId, payload) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('deleteExistingUserScopes'));
    
    try {
        const body = {
            'clientId': null,
            'siteId':   null
        };


        await k2aBaseApi.put(
            DeleteUserScopes.expand({
                userId,
                companyId
            }),
            body,
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('deleteExistingUserScopes'));
        dispatch(setProgressBarLoading(false));
    }
};
